import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ArrowRight, ShieldCheck, Zap, BarChart3, Mail, RotateCw } from "lucide-react";
import GoogleButton from "@/components/GoogleButton";
import LegalStrip from "@/components/LegalStrip";
import { sanitizePhone, formatPhoneTR, isValidTurkishPhone } from "@/lib/validators";
import { tokenStorage } from "@/lib/tokenStorage";

export default function Signup() {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [step, setStep] = useState("form"); // form | verify
  const [form, setForm] = useState({ shop_name: "", owner_name: "", email: "", password: "", phone: "" });
  const [confirm, setConfirm] = useState("");
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== confirm) return toast.error(t("toast.passwords_mismatch"));
    if (!kvkkAccepted) return toast.error(t("signup.kvkk_required"));
    if (form.phone && !isValidTurkishPhone(form.phone)) {
      return toast.error("Telefon 10 haneli TR numarası olmalı (örn: 532 123 45 67)");
    }
    setLoading(true);
    try {
      await api.post("/auth/signup", { ...form, phone: sanitizePhone(form.phone), kvkk_accepted: true });
      toast.success(t("verify.subtitle"));
      setStep("verify");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const phoneInvalid = form.phone.length > 0 && !isValidTurkishPhone(form.phone);

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-email", { email: form.email, code: code.trim() });
      tokenStorage.set("uc_token", r.data.access_token);
      await refreshUser();
      toast.success("🎉 30 günlük ücretsiz deneme başladı. Tüm özellikler kullanıma hazır.", { duration: 6000 });
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: form.email });
      toast.success(t("verify.resent"));
    } catch {
      toast.error(t("common.error"));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="signup-page">
      <div className="hidden lg:flex flex-col justify-between p-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1583902340370-6a3fc847d373?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxnb2xkJTIwY29pbiUyMHN0YWNrfGVufDB8fHx8MTc4NTc1MDE5OXww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(5,5,5,0.85), rgba(5,5,5,0.6) 60%, rgba(5,5,5,0.95))" }} />

        <div className="relative z-10">
          <div className="font-display text-4xl tracking-widest" style={{color:"var(--gold)"}}>MİLYEMSOFT</div>
          <div className="label-caps mt-2">{t("app.tagline")}</div>
        </div>

        <div className="relative z-10 max-w-md space-y-8">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight font-light">
            {t("hero.headline1")}<br /><span style={{color:"var(--gold)"}}>{t("hero.headline2")}</span> {t("hero.headline3")}
          </h1>
          <div className="space-y-4">
            {[
              { icon: Zap, title: t("hero.feature1_title"), desc: t("hero.feature1_desc") },
              { icon: ShieldCheck, title: t("hero.feature2_title"), desc: t("hero.feature2_desc") },
              { icon: BarChart3, title: t("hero.feature3_title"), desc: t("hero.feature3_desc") },
            ].map(f => (
              <div key={f.title} className="flex gap-4">
                <f.icon size={20} style={{color:"var(--gold)"}} className="mt-1 shrink-0" />
                <div>
                  <div className="text-sm">{f.title}</div>
                  <div className="text-xs text-neutral-400">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 label-caps">{t("footer.copyright")}</div>
      </div>

      <div className="flex flex-col lg:items-center lg:justify-center p-6 sm:p-8 lg:p-16 bg-[#0a0a0a] relative min-h-screen">
        <div className="absolute right-4 sm:right-6 z-20 safe-top-abs"><LanguageSwitcher compact /></div>

        {/* Mobile brand strip */}
        <div className="lg:hidden absolute left-4 sm:left-6 z-20 safe-top-abs">
          <div className="font-display text-xl tracking-widest" style={{color:"var(--gold)"}}>MİLYEMSOFT</div>
        </div>

        <div className="order-last lg:order-none lg:absolute lg:bottom-0 lg:left-0 lg:right-0 lg:px-8 mt-auto pt-8 w-full"><LegalStrip /></div>

        {step === "form" && (
          <form onSubmit={submit} className="w-full max-w-sm space-y-5 safe-top-form" data-testid="signup-form">
            <div>
              <div className="label-caps">{t("auth.signup")}</div>
              <h2 className="font-display text-3xl mt-2">{t("signup.title")}</h2>
              <p className="text-xs text-neutral-500 mt-2">{t("signup.subtitle")}</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="label-caps block mb-1">{t("signup.shop_name")}</label>
                <input required value={form.shop_name} onChange={(e)=>setForm({...form, shop_name: e.target.value})} className="w-full bg-[--surface] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="signup-shop" style={{background:"var(--surface)"}} />
              </div>
              <div>
                <label className="label-caps block mb-1">{t("signup.owner_name")}</label>
                <input required value={form.owner_name} onChange={(e)=>setForm({...form, owner_name: e.target.value})} className="w-full bg-[--surface] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="signup-name" style={{background:"var(--surface)"}} />
              </div>
              <div>
                <label className="label-caps block mb-1">{t("auth.email")}</label>
                <input required type="email" inputMode="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="w-full bg-[--surface] border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="signup-email" style={{background:"var(--surface)"}} />
              </div>
              <div>
                <label className="label-caps block mb-1">{t("signup.phone")}</label>
                <input
                  value={formatPhoneTR(form.phone)}
                  onChange={(e)=>setForm({...form, phone: sanitizePhone(e.target.value)})}
                  inputMode="tel"
                  maxLength={13}
                  placeholder="532 123 45 67"
                  className={`w-full bg-[--surface] border px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold] ${
                    phoneInvalid ? "border-red-500/60" : "border-white/10"
                  }`}
                  data-testid="signup-phone"
                  style={{background:"var(--surface)"}}
                />
                {phoneInvalid && (
                  <div className="mt-1 text-[10px] text-red-400/90" data-testid="signup-phone-error">
                    10 haneli TR numarası girin
                  </div>
                )}
              </div>
              <div>
                <label className="label-caps block mb-1">{t("auth.password")}</label>
                <input required type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} className="w-full bg-[--surface] border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="signup-password" style={{background:"var(--surface)"}} />
                <div className="text-xs text-neutral-500 mt-1">{t("signup.password_hint")}</div>
              </div>
              <div>
                <label className="label-caps block mb-1">{t("auth.password_confirm")}</label>
                <input required type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)} className="w-full bg-[--surface] border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="signup-confirm" style={{background:"var(--surface)"}} />
              </div>
            </div>

            <button type="submit" disabled={loading || !kvkkAccepted || phoneInvalid} className="btn-primary-gold w-full flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="signup-submit">
              {loading ? t("signup.creating") : <>30 Gün Ücretsiz Deneme Başlat <ArrowRight size={14} /></>}
            </button>
            <div className="text-[11px] text-center text-neutral-500 mt-2">Kredi kartı istenmez · İstediğin an iptal et · 30. günün sonunda ödeme</div>

            {/* KVKK + Mesafeli Satış onay kutusu (zorunlu) */}
            <label className="flex items-start gap-2.5 pt-1 cursor-pointer group" data-testid="signup-kvkk-wrapper">
              <input
                type="checkbox"
                checked={kvkkAccepted}
                onChange={(e) => setKvkkAccepted(e.target.checked)}
                required
                data-testid="signup-kvkk-checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-[--gold] border-white/20 bg-transparent"
                style={{ accentColor: "var(--gold)" }}
              />
              <span className="text-[11px] text-neutral-400 leading-relaxed group-hover:text-neutral-300">
                <Link to="/legal/gizlilik-kvkk" target="_blank" rel="noreferrer" className="hover:text-white" style={{color:"var(--gold)"}}>
                  KVKK Aydınlatma Metni
                </Link>
                {" ve "}
                <Link to="/legal/mesafeli-satis" target="_blank" rel="noreferrer" className="hover:text-white" style={{color:"var(--gold)"}}>
                  Mesafeli Satış Sözleşmesi
                </Link>
                {" ve "}
                <Link to="/legal/kullanim-kosullari" target="_blank" rel="noreferrer" className="hover:text-white" style={{color:"var(--gold)"}}>
                  Kullanım Koşulları
                </Link>
                'nı okudum, kabul ediyorum. Kişisel verilerimin abonelik ve destek amacıyla işlenmesine izin veriyorum.
              </span>
            </label>

            <div className="relative flex items-center py-1">
              <div className="flex-1 border-t border-white/10"></div>
              <span className="px-3 text-[10px] uppercase tracking-widest text-neutral-600">{t("auth.or_divider")}</span>
              <div className="flex-1 border-t border-white/10"></div>
            </div>

            <GoogleButton testId="google-signin-btn-signup" />

            <div className="text-center text-xs text-neutral-500 pt-2">
              {t("auth.have_account")} <Link to="/login" className="hover:text-[--gold]" style={{color:"var(--gold)"}} data-testid="link-login">{t("auth.login")}</Link>
            </div>
          </form>
        )}

        {step === "verify" && (
          <form onSubmit={verify} className="w-full max-w-sm space-y-5 pt-12 lg:pt-0" data-testid="verify-form">
            <div>
              <div className="w-12 h-12 border flex items-center justify-center mb-4" style={{borderColor:"var(--gold)", color:"var(--gold)"}}>
                <Mail size={22} />
              </div>
              <div className="label-caps">{t("auth.signup")}</div>
              <h2 className="font-display text-3xl mt-2">{t("verify.title")}</h2>
              <p className="text-xs text-neutral-400 mt-2 leading-relaxed">{t("verify.subtitle")}</p>
              <p className="text-sm mt-3 font-mono" style={{color:"var(--gold)"}} data-testid="verify-email-display">{form.email}</p>
            </div>

            <div>
              <label className="label-caps block mb-2">{t("verify.code_label")}</label>
              <input
                required
                autoFocus
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e)=>setCode(e.target.value.replace(/[^0-9]/g,""))}
                className="w-full bg-[--surface] border border-white/10 px-4 py-4 text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-1 focus:ring-[--gold]"
                data-testid="verify-code-input"
                style={{background:"var(--surface)"}}
                placeholder="000000"
              />
            </div>

            <button type="submit" disabled={loading || code.length !== 6} className="btn-primary-gold w-full" data-testid="verify-submit">
              {loading ? t("auth.connecting") : t("verify.submit")}
            </button>

            <div className="flex items-center justify-between text-xs pt-2 border-t border-white/8 gap-2">
              <button type="button" onClick={resend} disabled={resending} className="flex items-center gap-1.5 text-neutral-400 hover:text-[--gold]" data-testid="verify-resend">
                <RotateCw size={12} className={resending ? "animate-spin" : ""} /> {t("verify.resend")}
              </button>
              <button type="button" onClick={()=>setStep("form")} className="text-neutral-500 hover:text-white" data-testid="verify-change-email">
                {t("verify.change_email")}
              </button>
            </div>

            <div className="text-[11px] text-neutral-600 leading-relaxed pt-2">{t("verify.spam_hint")}</div>
          </form>
        )}
      </div>
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}
